/**
 * @file utils/headers/library.js
 * @description
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 */

import { CONTENT_TYPE_KEY, VALID_CONTENT_TYPE } from "./constants";
import { HTTP_OK } from "../../constants/http";

/**
 * Predicate to check if a response's status and content-type are valid
 *
 * @param {Object} response the response object
 * @param {number} response.status the status code of the response
 * @param {Object} response.headers the headers of the response
 *
 * @returns {boolean} True if response properties are valid, false otherwise
 */
export const isResponseValid = (response) => {
    if (!response || !response.headers || !response.status) {
        return false;
    }
    const { status, headers } = response;
    const key = CONTENT_TYPE_KEY; // Check headers for 'content-type' property

    return (
        checkValidStatusCode(status) &&
        checkValidContentType(getHeadersProperty({ headers, key }))
    );
};

/**
 * Predicate to check if response status code is valid
 *
 * @param {number} status the response status code
 *
 * @returns {boolean} True if status code is 200, false otherwise
 */
export const checkValidStatusCode = (status) => status === HTTP_OK;

/**
 * Checks to see if a given key is in the response object
 *
 * @param {Object} headers the response headers
 * @param {string} key property key to grab from response object
 *
 * @returns {string} The content-type property from the headers
 */
export const getHeadersProperty = ({ headers, key }) => headers.get(key);

/**
 * Takes a Content-Type from headers and checks if it's application/json
 *
 * @param {string} type the content-type from the response
 *
 * @returns {boolean} True if content-type is application/json, false otherwise
 */
export const checkValidContentType = (type) => {
    const cleanedType = cleanContentType(type);

    return VALID_CONTENT_TYPE.includes(cleanedType);
};

/**
 * Prepares a content-type to compare by removing whitespace and switching to lowercase
 *
 * @param {string} type the content-type value
 *
 * @returns {string} The cleaned content-type
 */
export const cleanContentType = (type) => {
    return type && type.replace(" ", "").toLowerCase();
};
